/**
 * EntityHistoryTab Component (AC-573)
 *
 * Displays change history for a specific entity (agent, model, workflow, or queue)
 * within entity editor modals. Unlike HistoryPage, this component does NOT have
 * an entity type filter dropdown since it already shows only one entity's history.
 *
 * Parent Components: AgentFormModal, ModelFormModal, WorkflowEditor, QueueDetailsPage (as tabs/sections)
 *
 * Features:
 * - Loads entity-specific history on mount
 * - Refreshes when entityType or entityId changes
 * - Shows "Save entity first" message for unsaved (null entityId) entities
 * - Shows loading, error, and empty states
 * - Integrates with ChangeList for displaying changes
 * - Supports re-apply functionality via ChangeList
 *
 * Props:
 * - entityType: 'model' | 'agent' | 'orchestration' | 'queue'
 * - entityId: number | null (null for unsaved entities)
 * - projectId: number
 *
 * Related Tickets:
 * - AC-563 (Epic: Project Version Control)
 * - AC-573 (Frontend - EntityHistoryTab component)
 */

import React, { useEffect, useCallback } from 'react';
import { useVersionStore } from '../../store/versionStore';
import ChangeList from './ChangeList';
import { ChangeSummary } from '../../types/version_control';

export interface EntityHistoryTabProps {
  entityType: 'model' | 'agent' | 'orchestration' | 'queue';
  entityId: number | null;
  projectId: number;
  /** ISO timestamp - changes newer than this will be highlighted as "New" */
  highlightChangesSince?: string | null;
  /** Callback when entity is changed via undo/reapply - parent should refresh entity data */
  onEntityChanged?: () => void;
  /** Use compact layout for narrow panels (default: true) */
  compact?: boolean;
}

const EntityHistoryTab: React.FC<EntityHistoryTabProps> = ({
  entityType,
  entityId,
  projectId,
  highlightChangesSince,
  onEntityChanged,
  compact = true,
}) => {
  const {
    loadEntityHistory,
    entityHistory,
    changesLoading,
    changesError,
  } = useVersionStore();

  // Generate the key for this entity's history in the store's Map
  const historyKey = `${entityType}_${entityId}`;

  // Get changes for this specific entity from the store
  const changes: ChangeSummary[] = entityId !== null
    ? (entityHistory.get(historyKey) || [])
    : [];

  // Load entity history on mount and when entity changes
  useEffect(() => {
    if (entityId === null) {
      // Don't load history for unsaved entities
      return;
    }

    loadEntityHistory(entityType, entityId);
  }, [entityType, entityId, loadEntityHistory]);

  // Wrap onEntityChanged to also reload history after undo/reapply
  const handleEntityChanged = useCallback(() => {
    onEntityChanged?.();
    if (entityId !== null) {
      loadEntityHistory(entityType, entityId);
    }
  }, [onEntityChanged, entityType, entityId, loadEntityHistory]);

  // Handle unsaved entity (null entityId)
  if (entityId === null) {
    return (
      <div className="p-4 text-center">
        <p className="text-gray-500 dark:text-gray-400">
          Save the entity first to see its history
        </p>
      </div>
    );
  }

  // Handle loading state
  if (changesLoading && changes.length === 0) {
    return (
      <ChangeList
        changes={[]}
        projectId={projectId}
        loading={true}
        error={null}
      />
    );
  }

  // Handle error state
  if (changesError) {
    return (
      <div className="p-4">
        <p className="text-red-600">Failed to load history: {changesError}</p>
      </div>
    );
  }

  // Handle empty state
  if (changes.length === 0) {
    return (
      <div className="p-4 text-center">
        <p className="text-gray-500 dark:text-gray-400">No history yet</p>
        <p className="text-sm text-gray-400 mt-1">
          Changes will appear here when you update this {entityType}
        </p>
      </div>
    );
  }

  // Render ChangeList with entity's changes
  // Note: We pass the changes directly, and ChangeList will handle re-apply
  // The onFilterChange prop is intentionally NOT passed since EntityHistoryTab
  // does not need filtering (already filtered to one entity)
  // Use compact mode for narrow panel display
  // Pass highlightChangesSince to show "New" badges on recent changes
  // Pass onEntityChanged to notify parent when entity is modified via undo/reapply
  return (
    <div className="entity-history-tab">
      <ChangeList
        changes={changes}
        projectId={projectId}
        loading={changesLoading}
        error={changesError}
        compact={compact}
        highlightChangesSince={highlightChangesSince}
        onEntityChanged={handleEntityChanged}
      />
    </div>
  );
};

export default EntityHistoryTab;
